import React, { useState, useEffect, useCallback } from 'react';
import { closedWonAPI, usersAPI } from '../../services/api';
import RequiredFieldLabel from '../Common/RequiredFieldLabel';
import { validateClosedWon, hasValidationErrors, getErrorMessage, isClosedWonFieldRequired } from '../../utils/validation';

function UserClosedWon() {
  const currentUserId = localStorage.getItem('id');
  const userRole = localStorage.getItem('role');
  const [wonLeads, setWonLeads] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 12
  });
  const [filters, setFilters] = useState({
    search: '',
    contractType: 'all',
    onboardingStatus: 'all',
    paymentSchedule: 'all',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [selectedWon, setSelectedWon] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [wonData, setWonData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [canEdit, setCanEdit] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getPublicUsers();
      // Users fetched but not used in current implementation
      console.log('Users loaded:', response.data?.length || 0);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchWonLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...filters,
        page: pagination.currentPage,
        limit: pagination.limit
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === 'all') {
          delete params[key];
        }
      });
      
      const response = await closedWonAPI.getAll(params);
      setWonLeads(response.data.closedWonRecords || []);
      setTotalRevenue(response.data.totalRevenue || 0);
      if (response.data.pagination) {
        setPagination(prev => ({ ...prev, ...response.data.pagination }));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch won records');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.currentPage, pagination.limit]);

  useEffect(() => {
    fetchWonLeads();
    fetchUsers();
  }, [fetchWonLeads]);

  const openModal = (won) => {
    setSelectedWon(won);
    
    // Check if user can edit this won record
    const isAssigned = won.lead?.primaryAssignee?._id === currentUserId ||
                      won.lead?.secondaryAssignees?.some(assignee => assignee._id === currentUserId);
    setCanEdit(userRole === 'admin' || isAssigned);
    
    setWonData({
      dealValue: won.dealValue || '',
      contractStartDate: won.contractStartDate ? new Date(won.contractStartDate).toISOString().split('T')[0] : '',
      contractEndDate: won.contractEndDate ? new Date(won.contractEndDate).toISOString().split('T')[0] : '',
      contractDuration: won.contractDuration || '',
      paymentTerms: won.paymentTerms || '',
      paymentSchedule: won.paymentSchedule || 'Annual',
      discountApplied: won.discountApplied || 0,
      currency: won.currency || 'USD',
      contractType: won.contractType || 'New Business',
      productsSold: won.productsSold?.join(', ') || '',
      implementationDate: won.implementationDate ? new Date(won.implementationDate).toISOString().split('T')[0] : '',
      goLiveDate: won.goLiveDate ? new Date(won.goLiveDate).toISOString().split('T')[0] : '',
      accountManager: won.accountManager?._id || '',
      customerSuccessManager: won.customerSuccessManager?._id || '',
      onboardingStatus: won.onboardingStatus || 'Not Started',
      renewalDate: won.renewalDate ? new Date(won.renewalDate).toISOString().split('T')[0] : '',
      upsellOpportunities: won.upsellOpportunities || '',
      customerSatisfactionScore: won.customerSatisfactionScore || '',
      referralPotential: won.referralPotential || 'Medium',
      contractDocument: won.contractDocument || '',
      invoiceNumber: won.invoiceNumber || '',
      notes: won.notes || ''
    });
    setShowModal(true);
  };

  const saveWonData = async () => {
    const errors = validateClosedWon(wonData);
    setValidationErrors(errors);
    
    if (hasValidationErrors(errors)) {
      setError('Please fix the validation errors before saving.');
      return;
    }
    
    setLoading(true);
    try {
      const dataToSave = {
        ...wonData,
        productsSold: wonData.productsSold.split(',').map(product => product.trim()).filter(product => product),
        accountManager: wonData.accountManager || null,
        customerSuccessManager: wonData.customerSuccessManager || null
      };
      
      // Remove empty string values that cause ObjectId cast errors
      if (dataToSave.accountManager === '') delete dataToSave.accountManager;
      if (dataToSave.customerSuccessManager === '') delete dataToSave.customerSuccessManager;
      
      await closedWonAPI.update(selectedWon._id, dataToSave);
      setSuccess('Won lead information updated successfully!');
      setShowModal(false);
      setValidationErrors({});
      await fetchWonLeads();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update won information');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200/50 p-4 sm:p-5 md:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-green-600 bg-clip-text text-transparent">
              My Closed Won Deals
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mt-1">View and manage your successfully closed deals</p>
          </div>
        </div>

        {/* Revenue Summary */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-green-800">Total Revenue</h3>
              <p className="text-xl sm:text-2xl font-bold text-green-900">${totalRevenue.toLocaleString()}</p>
            </div>
            <div className="text-green-600">
              <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm sm:text-base">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl mb-6 text-sm sm:text-base">
            {success}
          </div>
        )}

        {/* Filters */}
        <div className="bg-gray-50 rounded-xl p-3 sm:p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
            <input
              type="text"
              placeholder="Search by school name..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="p-2.5 sm:p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-sm sm:text-base"
            />
            
            <select
              value={filters.contractType}
              onChange={(e) => handleFilterChange('contractType', e.target.value)}
              className="p-2.5 sm:p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-sm sm:text-base"
            >
              <option value="all">All Contract Types</option>
              <option value="New Business">New Business</option>
              <option value="Renewal">Renewal</option>
              <option value="Expansion">Expansion</option>
              <option value="Upgrade">Upgrade</option>
            </select>
            
            <select
              value={filters.onboardingStatus}
              onChange={(e) => handleFilterChange('onboardingStatus', e.target.value)}
              className="p-2.5 sm:p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-sm sm:text-base"
            >
              <option value="all">All Onboarding Status</option>
              <option value="Not Started">Not Started</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="On Hold">On Hold</option>
            </select>
            
            <select
              value={filters.paymentSchedule}
              onChange={(e) => handleFilterChange('paymentSchedule', e.target.value)}
              className="p-2.5 sm:p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-sm sm:text-base"
            >
              <option value="all">All Payment Schedules</option>
              <option value="Monthly">Monthly</option>
              <option value="Quarterly">Quarterly</option>
              <option value="Semi-Annual">Semi-Annual</option>
              <option value="Annual">Annual</option>
              <option value="One-time">One-time</option>
            </select>
          </div>
          
          <div className="flex justify-between items-center">
            <div className="text-xs sm:text-sm text-gray-600">
              Showing {wonLeads.length} of {pagination.totalCount} won deals
            </div>
          </div>
        </div>

        {loading && <p className="text-center py-8 text-sm sm:text-base">Loading won records...</p>}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
          {wonLeads.map((won) => (
            <div key={won._id} className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="flex justify-between items-start mb-4 gap-2">
                <div className="min-w-0 flex-1">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">{won.lead?.schoolName}</h3>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">{won.lead?.email}</p>
                </div>
                <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${
                  won.onboardingStatus === 'Completed' ? 'bg-green-100 text-green-800' :
                  won.onboardingStatus === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                  won.onboardingStatus === 'On Hold' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {won.onboardingStatus}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                <div className="text-xs sm:text-sm text-gray-600">
                  <span className="font-medium">Assignment:</span>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {won.lead?.primaryAssignee?._id === currentUserId && (
                      <div className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                        Primary Assignee
                      </div>
                    )}
                    {won.lead?.secondaryAssignees?.some(assignee => assignee._id === currentUserId) && (
                      <div className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                        Secondary Assignee
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-gray-600">
                  <span className="font-medium">Deal Value:</span> 
                  <span className="font-bold text-green-600 ml-1">₹{won.dealValue?.toLocaleString()}</span>
                </p>
                <p className="text-xs sm:text-sm text-gray-600">
                  <span className="font-medium">Final Price:</span> 
                  <span className="font-bold text-green-600 ml-1">₹{won.finalPrice?.toLocaleString()}</span>
                </p>
                <p className="text-xs sm:text-sm text-gray-600">
                  <span className="font-medium">Contract Type:</span> {won.contractType}
                </p>
              </div>

              <button
                onClick={() => openModal(won)}
                className={`w-full px-3 sm:px-4 py-2 rounded-lg transition text-xs sm:text-sm font-medium ${
                  userRole === 'admin' || won.lead?.primaryAssignee?._id === currentUserId || 
                  won.lead?.secondaryAssignees?.some(assignee => assignee._id === currentUserId)
                    ? 'bg-green-500 text-white hover:bg-green-600'
                    : 'bg-gray-500 text-white hover:bg-gray-600'
                }`}
              >
                {userRole === 'admin' || won.lead?.primaryAssignee?._id === currentUserId || 
                 won.lead?.secondaryAssignees?.some(assignee => assignee._id === currentUserId)
                  ? 'Manage Deal Info'
                  : 'View Deal Info'
                }
              </button>
            </div>
          ))}
        </div>

        {!loading && wonLeads.length === 0 && (
          <div className="text-center py-12">
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No Won Deals</h3>
            <p className="text-sm sm:text-base text-gray-500">No closed won deals found.</p>
          </div>
        )}

        {/* Pagination */}
        {!loading && pagination.totalPages > 1 && (
          <div className="flex justify-center items-center flex-wrap gap-2 mt-6">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={!pagination.hasPrev}
              className="px-2 sm:px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
            >
              Previous
            </button>
            
            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
              const pageNum = Math.max(1, pagination.currentPage - 2) + i;
              if (pageNum > pagination.totalPages) return null;
              
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-2 sm:px-3 py-2 rounded-lg border text-xs sm:text-sm ${
                    pageNum === pagination.currentPage
                      ? 'bg-green-500 text-white border-green-500'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasNext}
              className="px-2 sm:px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
            >
              Next
            </button>
          </div>
        )}

        {/* Modal */}
        {showModal && selectedWon && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-6">
                <div className="flex justify-between items-start mb-4 sm:mb-6 gap-2">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 pr-2">Deal Information - {selectedWon.lead?.schoolName}</h3>
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                  >
                    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div>
                    <RequiredFieldLabel required={isClosedWonFieldRequired('dealValue')}>Deal Value</RequiredFieldLabel>
                    <input
                      type="number"
                      value={wonData.dealValue}
                      onChange={(e) => canEdit && setWonData({...wonData, dealValue: e.target.value})}
                      className={`w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm sm:text-base ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''} ${getErrorMessage(validationErrors, 'dealValue') ? 'border-red-500' : ''}`}
                      readOnly={!canEdit}
                    />
                    {getErrorMessage(validationErrors, 'dealValue') && (
                      <p className="text-red-500 text-sm mt-1">{getErrorMessage(validationErrors, 'dealValue')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Contract Start Date</label>
                    <input
                      type="date"
                      value={wonData.contractStartDate}
                      onChange={(e) => canEdit && setWonData({...wonData, contractStartDate: e.target.value})}
                      className={`w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm sm:text-base ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      readOnly={!canEdit}
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Contract Type</label>
                    <select
                      value={wonData.contractType}
                      onChange={(e) => canEdit && setWonData({...wonData, contractType: e.target.value})}
                      className={`w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm sm:text-base ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      disabled={!canEdit}
                    >
                      <option value="New Business">New Business</option>
                      <option value="Renewal">Renewal</option>
                      <option value="Expansion">Expansion</option>
                      <option value="Upgrade">Upgrade</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Onboarding Status</label>
                    <select
                      value={wonData.onboardingStatus}
                      onChange={(e) => canEdit && setWonData({...wonData, onboardingStatus: e.target.value})}
                      className={`w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm sm:text-base ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      disabled={!canEdit}
                    >
                      <option value="Not Started">Not Started</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                      <option value="On Hold">On Hold</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Payment Terms</label>
                    <textarea
                      value={wonData.paymentTerms}
                      onChange={(e) => canEdit && setWonData({...wonData, paymentTerms: e.target.value})}
                      className={`w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 h-20 text-sm sm:text-base ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      readOnly={!canEdit}
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Notes</label>
                    <textarea
                      value={wonData.notes}
                      onChange={(e) => canEdit && setWonData({...wonData, notes: e.target.value})}
                      className={`w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 h-20 text-sm sm:text-base ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      readOnly={!canEdit}
                    />
                  </div>
                </div>

                {/* Lead History Section */}
                {selectedWon.lead && (
                  <div className="mb-4 sm:mb-6">
                    <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Lead History</h4>
                    
                    {/* Status History */}
                    {selectedWon.lead.statusHistory && selectedWon.lead.statusHistory.length > 0 && (
                      <div className="mb-4">
                        <h5 className="text-sm font-medium text-gray-700 mb-2">Status History</h5>
                        <div className="max-h-32 overflow-y-auto border rounded-lg">
                          {selectedWon.lead.statusHistory.map((history, index) => (
                            <div key={index} className="p-2 border-b last:border-b-0 text-xs">
                              <div className="flex justify-between items-start">
                                <div>
                                  <span className="font-medium">{history.oldStatus || 'New'} → {history.newStatus}</span>
                                  <p className="text-gray-600 mt-1">{history.description}</p>
                                </div>
                                <div className="text-right text-gray-500">
                                  <div>{history.changedBy?.username || 'System'}</div>
                                  <div>{new Date(history.updatedAt).toLocaleDateString()}</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Rating History */}
                    {selectedWon.lead.ratingHistory && selectedWon.lead.ratingHistory.length > 0 && (
                      <div className="mb-4">
                        <h5 className="text-sm font-medium text-gray-700 mb-2">Rating History</h5>
                        <div className="max-h-32 overflow-y-auto border rounded-lg">
                          {selectedWon.lead.ratingHistory.map((rating, index) => (
                            <div key={index} className="p-2 border-b last:border-b-0 text-xs">
                              <div className="flex justify-between items-start">
                                <div>
                                  <span className="font-medium">Rating: {rating.rating}/10</span>
                                  <p className="text-gray-600 mt-1">{rating.reason}</p>
                                </div>
                                <div className="text-right text-gray-500">
                                  <div>{rating.givenBy?.username || 'System'}</div>
                                  <div>{new Date(rating.createdAt).toLocaleDateString()}</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Assignment History */}
                    {selectedWon.lead.assignmentHistory && selectedWon.lead.assignmentHistory.length > 0 && (
                      <div>
                        <h5 className="text-sm font-medium text-gray-700 mb-2">Assignment History</h5>
                        <div className="max-h-32 overflow-y-auto border rounded-lg">
                          {selectedWon.lead.assignmentHistory.map((assignment, index) => (
                            <div key={index} className="p-2 border-b last:border-b-0 text-xs">
                              <div className="flex justify-between items-start">
                                <div>
                                  <span className="font-medium capitalize">{assignment.type} Assignment</span>
                                  <p className="text-gray-600 mt-1">
                                    {assignment.type === 'primary' ? (
                                      `${assignment.oldAssignee?.username || 'None'} → ${assignment.newAssignee?.username || 'None'}`
                                    ) : (
                                      `Secondary assignees updated`
                                    )}
                                  </p>
                                </div>
                                <div className="text-right text-gray-500">
                                  <div>{assignment.changedBy?.username || 'System'}</div>
                                  <div>{new Date(assignment.changedAt).toLocaleDateString()}</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3">
                  {canEdit ? (
                    <>
                      <button
                        onClick={saveWonData}
                        disabled={loading}
                        className="flex-1 bg-green-500 text-white px-4 py-2.5 rounded-lg hover:bg-green-600 transition disabled:opacity-50 font-medium text-sm sm:text-base"
                      >
                        {loading ? 'Saving...' : 'Save Deal Info'}
                      </button>
                      <button
                        onClick={() => setShowModal(false)}
                        className="flex-1 bg-gray-100 text-gray-700 px-4 py-2.5 rounded-lg hover:bg-gray-200 transition font-medium text-sm sm:text-base"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-2.5 rounded-lg text-center font-medium text-xs sm:text-sm">
                        Read-Only: You can only view this information
                      </div>
                      <button
                        onClick={() => setShowModal(false)}
                        className="flex-1 bg-gray-100 text-gray-700 px-4 py-2.5 rounded-lg hover:bg-gray-200 transition font-medium text-sm sm:text-base"
                      >
                        Close
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserClosedWon;